/**
 * Skills API Service
 *
 * Provides methods for fetching and managing user skills with real backend integration.
 * All API calls include JWT authentication and proper error handling.
 *
 * Backend Endpoints (prefixed with /api/v1):
 * - GET /skills/taxonomy - Returns skill taxonomy with categories
 * - GET /skills/taxonomy/v2 - Returns skill taxonomy organized by domain
 * - GET /my-skills/v2 - Returns user's skills with domain groupings and stats
 * - GET /my-skills/relationships/v2 - Returns graph data with domain hubs
 * - POST /my-skills - Add skill to user's profile
 * - PUT /my-skills/{skillId} - Update skill proficiency
 * - DELETE /my-skills/{skillId} - Remove skill from profile
 * - GET /skills/{skillId}/suggested-programs - Get learning programs for skill
 * - GET /skills/search/autocomplete - Search skills for autocomplete
 * - GET /domains - Get available skill domains
 *
 * @author Tutara Team
 * @since January 2025
 */

import { API_BASE_URL } from '@/config/env'
import { useAuthStore } from '@/stores/auth-store';
import type {
  Skill,
  SuggestedProgram,
  SkillTaxonomyCategory,
  AddSkillRequest,
  UpdateProficiencyRequest,
  ApiResponse,
  SkillsApiError,
  // V2 Types
  MySkillsV2Response,
  SkillGraphV2Response,
  SkillTaxonomyV2Response,
  DomainGroupDTO,
} from '@/types/skill';

// =============================================================================
// Configuration
// =============================================================================

const SKILLS_API_BASE = `${API_BASE_URL}/api/v1`;

// =============================================================================
// Autocomplete Types
// =============================================================================

/**
 * Skill suggestion from autocomplete API
 */
export interface SkillSuggestion {
  id: string;
  name: string;
  category: string;
  domain?: string;
  difficultyLevel?: string;
  isCoreSkill?: boolean;
  icon?: string;
  relevanceScore?: number;
}

/**
 * Autocomplete response structure
 */
export interface SkillAutocompleteResponse {
  skills: SkillSuggestion[];
}

// =============================================================================
// Career Milestone Search Types
// =============================================================================

/**
 * Career milestone search result from the unified search API
 * Returns milestones across all career paths matching the search query
 */
export interface CareerMilestoneSearchResult {
  /** Milestone unique identifier */
  id: string;
  /** Milestone title (e.g., "Senior Software Engineer") */
  title: string;
  /** Milestone description */
  description: string;
  /** Career path type: 'ic' or 'management' */
  path: 'ic' | 'management';
  /** Level within the career path */
  level: number;
  /** Required years of experience for this milestone */
  requiredYearsExperience: number;
  /** Parent career path ID */
  careerPathId: string;
  /** Parent career path key (e.g., "full-stack-developer") */
  careerPathKey: string;
  /** Parent career path title (e.g., "Full Stack Developer") */
  careerPathTitle: string;
  /** Career path category (e.g., "tech", "business") */
  careerPathCategory: string;
  /** Career path icon identifier */
  careerPathIcon?: string;
  /** Career path color */
  careerPathColor?: string;
  /** Alias that matched the search query (if matched via alias) */
  matchedAlias?: string;
  /** Search relevance score (0-100) */
  matchScore?: number;
  /** Whether this milestone was auto-created by AI */
  isAiGenerated?: boolean;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get authentication headers from auth store
 */
function getAuthHeaders(): Record<string, string> {
  const { tokens, user } = useAuthStore.getState();

  if (!tokens?.accessToken) {
    throw new Error('Not authenticated');
  }

  const headers: Record<string, string> = {
    'Authorization': `Bearer ${tokens.accessToken}`,
    'Content-Type': 'application/json',
  };

  // Add user context headers if available
  if (user?.id) {
    headers['X-User-Id'] = user.id;
  }

  if (user?.organizationId) {
    headers['X-Organization-Id'] = user.organizationId;
  }

  if (user?.tenantId) {
    headers['X-Tenant-Id'] = user.tenantId;
  }

  return headers;
}

/**
 * Request helper with comprehensive error handling
 */
async function fetchWithErrorHandling<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...getAuthHeaders(),
        ...options.headers,
      },
    });

    // Handle 204 No Content response
    if (response.status === 204) {
      return {} as T;
    }

    // Parse JSON response
    const text = await response.text();
    const data = text ? JSON.parse(text) : {};

    // Handle error responses
    if (!response.ok) {
      const error: SkillsApiError = {
        message: data.message || data.detail || `HTTP ${response.status}: ${response.statusText}`,
        status: response.status,
        code: data.code,
      };
      throw error;
    }

    return data as T;
  } catch (error) {
    if ((error as SkillsApiError).status !== undefined) {
      throw error;
    }

    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw {
        message: 'Unable to connect to the server. Please check your connection.',
        status: 0,
      } as SkillsApiError;
    }

    throw {
      message: error instanceof Error ? error.message : 'An unexpected error occurred',
      status: 0,
    } as SkillsApiError;
  }
}


// =============================================================================
// API Methods
// =============================================================================

/**
 * Get skill taxonomy with categories and skills
 * Used for skill selection/addition UI
 *
 * @returns Array of skill taxonomy categories
 *
 * @example
 * const taxonomy = await getSkillTaxonomy();
 * // Returns: [{ category: 'Programming', skills: [...] }, ...]
 */
export async function getSkillTaxonomy(): Promise<SkillTaxonomyCategory[]> {
  const response = await fetchWithErrorHandling<ApiResponse<SkillTaxonomyCategory[]>>(
    `${SKILLS_API_BASE}/skills/taxonomy`,
    { method: 'GET' }
  );
  return response.data || [];
}



/**
 * Get detailed information about a specific skill
 *
 * @param skillId - Skill UUID
 * @returns Skill details or null if not found
 *
 * @example
 * const skill = await getSkillDetails('javascript-uuid');
 */
export async function getSkillDetails(skillId: string): Promise<Skill | null> {
  try {
    const response = await fetchWithErrorHandling<ApiResponse<Skill>>(
      `${SKILLS_API_BASE}/skills/${skillId}`,
      { method: 'GET' }
    );
    return response.data || null;
  } catch (error) {
    // Return null for 404 errors
    if ((error as SkillsApiError).status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * Add a skill to user's profile
 *
 * @param request - Skill addition request with skillId and optional proficiency
 * @returns The added skill
 *
 * @example
 * await addSkill({ skillId: 'typescript-uuid', proficiencyLevel: 50, status: 'in_progress' });
 */
export async function addSkill(request: AddSkillRequest): Promise<Skill> {
  const response = await fetchWithErrorHandling<ApiResponse<Skill>>(
    `${SKILLS_API_BASE}/my-skills`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );
  return response.data;
}

/**
 * Add a skill to the user's desired skills list
 * Convenience wrapper for addSkill with desired status
 *
 * @param skillId - Skill UUID to add as desired
 *
 * @example
 * await addDesiredSkill('kubernetes-uuid');
 */
export async function addDesiredSkill(skillId: string): Promise<Skill> {
  return addSkill({ skillId, status: 'desired', proficiencyLevel: 0 });
}

/**
 * Update the proficiency level of a skill
 *
 * @param skillId - Skill UUID
 * @param level - New proficiency level (0-100)
 * @returns Updated skill
 *
 * @example
 * await updateProficiency('javascript-uuid', 85);
 */
export async function updateProficiency(
  skillId: string,
  level: number
): Promise<Skill> {
  const request: UpdateProficiencyRequest = { proficiencyLevel: level };

  const response = await fetchWithErrorHandling<ApiResponse<Skill>>(
    `${SKILLS_API_BASE}/my-skills/${skillId}`,
    {
      method: 'PUT',
      body: JSON.stringify(request),
    }
  );
  return response.data;
}

/**
 * Remove a skill from the user's profile
 *
 * @param skillId - Skill UUID to remove
 *
 * @example
 * await removeSkill('old-skill-uuid');
 */
export async function removeSkill(skillId: string): Promise<void> {
  await fetchWithErrorHandling<void>(
    `${SKILLS_API_BASE}/my-skills/${skillId}`,
    { method: 'DELETE' }
  );
}

/**
 * Get suggested learning programs for a specific skill
 *
 * @param skillId - Skill UUID
 * @returns Array of suggested programs with match scores
 *
 * @example
 * const programs = await getSuggestedPrograms('react-uuid');
 * programs.forEach(p => console.log(`${p.title} - ${p.matchScore}% match`));
 */
export async function getSuggestedPrograms(
  skillId: string
): Promise<SuggestedProgram[]> {
  const response = await fetchWithErrorHandling<ApiResponse<SuggestedProgram[]>>(
    `${SKILLS_API_BASE}/skills/${skillId}/suggested-programs`,
    { method: 'GET' }
  );
  return response.data || [];
}

/**
 * Search for available skills to add
 * Filters from taxonomy based on query
 *
 * @param query - Search query string
 * @returns Matching skills from taxonomy
 *
 * @example
 * const results = await searchAvailableSkills('react');
 */
export async function searchAvailableSkills(query: string): Promise<Skill[]> {
  if (!query.trim()) return [];

  try {
    // Get taxonomy and search through it
    const taxonomy = await getSkillTaxonomy();

    const lowerQuery = query.toLowerCase();
    const results: Skill[] = [];

    taxonomy.forEach((category) => {
      category.skills.forEach((skill) => {
        if (
          skill.name.toLowerCase().includes(lowerQuery) ||
          skill.category.toLowerCase().includes(lowerQuery) ||
          skill.description?.toLowerCase().includes(lowerQuery)
        ) {
          results.push({
            id: skill.id,
            name: skill.name,
            category: skill.category,
            description: skill.description,
            proficiencyLevel: 0,
            status: 'not_started',
            relatedSkills: [],
            prerequisites: [],
          });
        }
      });
    });

    return results;
  } catch (error) {
    console.error('Error searching skills:', error);
    return [];
  }
}

// =============================================================================
// V2 API Methods
// =============================================================================

/**
 * Get skill taxonomy in V2 format organized by domain
 *
 * @returns V2 skill taxonomy with domain groupings
 *
 * @example
 * const taxonomy = await getSkillTaxonomyV2();
 * // Returns: { domains: [...], totalSkills, totalDomains }
 */
export async function getSkillTaxonomyV2(): Promise<SkillTaxonomyV2Response> {
  const response = await fetchWithErrorHandling<ApiResponse<SkillTaxonomyV2Response>>(
    `${SKILLS_API_BASE}/skills/taxonomy/v2`,
    { method: 'GET' }
  );
  return response.data || { domains: [], totalSkills: 0, totalDomains: 0 };
}

/**
 * Get user's skills in V2 format with domain groupings and stats
 *
 * @returns V2 user's skills with domain groups and statistics
 *
 * @example
 * const skills = await getMySkillsV2();
 * console.log(skills.stats.progress); // Overall progress percentage
 */
export async function getMySkillsV2(): Promise<MySkillsV2Response> {
  const response = await fetchWithErrorHandling<ApiResponse<MySkillsV2Response>>(
    `${SKILLS_API_BASE}/my-skills/v2`,
    { method: 'GET' }
  );
  return response.data || { skills: [], domainGroups: [], stats: { totalSkills: 0, learnedSkills: 0, inProgressSkills: 0, desiredSkills: 0, progress: 0 } };
}

/**
 * Get skill graph data in V2 format for visualization
 * Returns nodes (domain hubs + skills) and edges (domain-skill, prerequisite, related)
 *
 * @returns V2 skill graph with domain hubs and typed edges
 *
 * @example
 * const graph = await getSkillGraphV2();
 * // Use graph.nodes.domainHubs and graph.nodes.skills with React Flow
 */
export async function getSkillGraphV2(): Promise<SkillGraphV2Response> {
  const response = await fetchWithErrorHandling<ApiResponse<SkillGraphV2Response>>(
    `${SKILLS_API_BASE}/my-skills/relationships/v2`,
    { method: 'GET' }
  );
  return response.data || {
    nodes: { domainHubs: [], skills: [] },
    edges: { domainSkillEdges: [], prerequisiteEdges: [], relatedEdges: [] }
  };
}

/**
 * Get all available skill domains
 *
 * @returns List of domains with skill counts
 *
 * @example
 * const domains = await getDomains();
 * domains.forEach(d => console.log(`${d.label}: ${d.totalCount} skills`));
 */
export async function getDomains(): Promise<DomainGroupDTO[]> {
  const response = await fetchWithErrorHandling<ApiResponse<DomainGroupDTO[]>>(
    `${SKILLS_API_BASE}/domains`,
    { method: 'GET' }
  );
  return response.data || [];
}

// =============================================================================
// Export API Object
// =============================================================================

// =============================================================================
// Skill Autocomplete API
// =============================================================================

/**
 * Search skills using the backend autocomplete API
 * Returns skills matching the query with relevance scoring
 *
 * @param query - Search query (minimum 1 character)
 * @param limit - Maximum results (default 10, max 50)
 * @returns Array of skill suggestions
 *
 * @example
 * const skills = await searchSkillsAutocomplete('react');
 * // Returns: [{ id: '...', name: 'React', category: 'Frontend', ... }, ...]
 */
export async function searchSkillsAutocomplete(
  query: string,
  limit: number = 10
): Promise<SkillSuggestion[]> {
  if (!query.trim()) return [];

  try {
    const response = await fetchWithErrorHandling<ApiResponse<SkillAutocompleteResponse>>(
      `${SKILLS_API_BASE}/skills/search/autocomplete?query=${encodeURIComponent(query.trim())}&limit=${limit}`,
      { method: 'GET' }
    );
    return response.data?.skills || [];
  } catch (error) {
    console.error('Error fetching skill autocomplete:', error);
    // Return empty array on error to allow graceful degradation
    return [];
  }
}

/**
 * Get all skill names for autocomplete suggestions
 * Fetches from taxonomy and returns just the names
 *
 * @returns Array of skill names
 */
export async function getAllSkillNames(): Promise<string[]> {
  try {
    const taxonomy = await getSkillTaxonomy();
    const names: string[] = [];
    taxonomy.forEach((category) => {
      category.skills.forEach((skill) => {
        names.push(skill.name);
      });
    });
    return names.sort();
  } catch (error) {
    console.error('Error fetching skill names:', error);
    return [];
  }
}

// =============================================================================
// Career Milestone Search API
// =============================================================================

/**
 * Search career milestones across all career paths
 * Returns milestones matching the query with career path context
 *
 * @param query - Search query (minimum 2 characters)
 * @param limit - Maximum results (default 10, max 50)
 * @returns Array of career milestone search results
 *
 * @example
 * const milestones = await searchCareerMilestones('senior engineer');
 * // Returns: [{ id: '...', title: 'Senior Software Engineer', careerPathTitle: 'Software Engineering', ... }]
 */
export async function searchCareerMilestones(
  query: string,
  limit: number = 10
): Promise<CareerMilestoneSearchResult[]> {
  if (!query.trim() || query.trim().length < 2) return [];

  try {
    const response = await fetchWithErrorHandling<ApiResponse<CareerMilestoneSearchResult[]>>(
      `${SKILLS_API_BASE}/career-paths/career-milestones/search?query=${encodeURIComponent(query.trim())}&limit=${limit}`,
      { method: 'GET' }
    );
    return response.data || [];
  } catch (error) {
    console.error('Error searching career milestones:', error);
    // Return empty array on error to allow graceful degradation
    return [];
  }
}

/**
 * Resolve a career goal to the best matching milestone using AI.
 * Called when standard search returns no results. Uses LLM to find the
 * most contextually appropriate milestone based on the career goal text.
 *
 * @param goalText - The career goal text (e.g., "CEO", "CTO")
 * @param currentRole - Optional current role for context
 * @returns The AI-suggested milestone search result, or null if no match
 */
export async function aiResolveCareerGoal(
  goalText: string,
  currentRole?: string
): Promise<CareerMilestoneSearchResult | null> {
  if (!goalText.trim()) return null;

  try {
    const params = new URLSearchParams({ goalText: goalText.trim() });
    if (currentRole) {
      params.append('currentRole', currentRole);
    }

    const response = await fetchWithErrorHandling<ApiResponse<CareerMilestoneSearchResult | null>>(
      `${SKILLS_API_BASE}/career-paths/career-milestones/ai-resolve?${params.toString()}`,
      { method: 'POST' }
    );
    return response.data || null;
  } catch (error) {
    console.error('Error in AI career goal resolution:', error);
    return null;
  }
}

const skillsApi = {
  // Taxonomy
  getSkillTaxonomy,
  getSkillTaxonomyV2,

  // V2 User skills - read (standardized)
  getMySkillsV2,
  getSkillGraphV2,
  getDomains,
  getSkillDetails,

  // User skills - write
  addSkill,
  addDesiredSkill,
  updateProficiency,
  removeSkill,

  // Discovery
  getSuggestedPrograms,
  searchAvailableSkills,

  // Autocomplete
  searchSkillsAutocomplete,
  getAllSkillNames,

  // Career Milestone Search
  searchCareerMilestones,
};

export default skillsApi;
